import NewButton from '$tablet-app/src/app/components/Button/NewButton';
import Card from '$tablet-app/src/app/components/Card';
import Chip from '$tablet-app/src/app/components/Chip';
import {
  FloatingActions,
  useFloatingActionControl,
} from '$tablet-app/src/app/components/FloatingActions';
import FullScreenLoader from '$tablet-app/src/app/components/FullScreenLoader';
import { Panel } from '$tablet-app/src/app/components/Panel';
import Row from '$tablet-app/src/app/components/Row';
import { ScreenBackButton } from '$tablet-app/src/app/components/ScreenBackButton';
import { ScreenDeleteButton } from '$tablet-app/src/app/components/ScreenDeleteButton';
import { ScreenHeader } from '$tablet-app/src/app/components/ScreenHeader';
import { ScreenSaveButton } from '$tablet-app/src/app/components/ScreenSaveButton';
import { ScreenTitle } from '$tablet-app/src/app/components/ScreenTitle';
import Spacer from '$tablet-app/src/app/components/Spacer';
import Text from '$tablet-app/src/app/components/Text';
import TextInput from '$tablet-app/src/app/components/TextInput';
import { EmojiInputField } from '$tablet-app/src/app/forms/components/EmojiInputField';
import FormColorSection from '$tablet-app/src/app/forms/components/FormColorSection/FormColorSection';
import {
  getGoalPeriodText,
  toggleCategoryInGoal,
} from '$tablet-app/src/app/goals/functions';
import { OptionModel } from '$tablet-app/src/app/options/types';
import { Alert } from '@akin-home/alerts';
import { GoalPeriod } from '@akin-home/amplify';
import { Button } from '@akin-home/buttons/native';
import { LargeInput } from '@akin-home/forms/native';
import { Goal } from '@akin-home/goals';
import { useBool } from '@akin-home/utils/react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React from 'react';
import { TouchableOpacity, useWindowDimensions, View } from 'react-native';
import Animated from 'react-native-reanimated';
import styled from 'styled-components/native';
import GoalLibraryCard from '../GoalLibraryCard';
import ReminderFormRow from '../ReminderFormRow';

interface Props {
  goal: Goal;
  goal_suggestions?: Goal[];
  loadingSuggestions?: boolean;
  reminders?: Alert[];
  period_visible?: boolean;
  reminders_visible?: boolean;
  categories?: OptionModel[];
  disable_name_change?: boolean;
  onCancel?: () => void;
  onConfirm?: () => void;
  onChange?: (goal: Goal) => void;
  onDelete?: () => void;
  toggleShowPeriods?: () => void;
  toggleShowReminders?: () => void;

  /** Reminder functions */
  onAddReminder?: () => void;
  onUpdateReminder?: (reminder: Alert) => void;
  onDeleteReminder?: (reminder: Alert) => void;

  /** Override the reminders */
  setReminders?(reminders: Alert[]): void;

  onPressSuggestion?(suggestion: Goal): void;
  onPressViewSuggestions?(): void;
}

const emptyFunc = () => undefined;

const GoalFormScreenView = (props: Props) => {
  const {
    goal,
    reminders,
    period_visible,
    categories,
    reminders_visible,
    loadingSuggestions,
    goal_suggestions,
    onCancel,
    toggleShowPeriods,
    toggleShowReminders,
    onConfirm,
    onDelete,
    onChange = emptyFunc,
    onAddReminder,
    onUpdateReminder,
    onDeleteReminder,
    setReminders,
    onPressSuggestion,
    onPressViewSuggestions,
  } = props;

  const { width } = useWindowDimensions();
  const isSmall = width < 600;

  const headerText = goal.id === '' ? 'New goal' : 'Edit goal';
  const show_suggestions = goal.id === '';

  const { scrollHandler, floating_action_styles } = useFloatingActionControl();
  const [colors_expanded, expandColors, collapseColors] = useBool();

  function updateReminders(update: Partial<Alert>) {
    if (reminders) {
      setReminders?.(reminders.map((reminder) => ({ ...reminder, ...update })));
    }
  }

  const onChangeTitle = (title: string) => {
    onChange({ ...goal, title });
    updateReminders({ title });
  };

  const onUpdateCount = (amount: number) => () =>
    onChange({
      ...goal,
      completions_per_period: goal.completions_per_period + amount || 1,
    });

  const onPressPeriod = (period: GoalPeriod) => () =>
    onChange({ ...goal, period });

  const onChangeEmoji = (emoji: string) => {
    onChange({ ...goal, emoji });
    updateReminders({ emoji });
  };

  const onChangeReferenceCode = (reference_code: string) =>
    onChange({ ...goal, reference_code });

  const onPressCategory = (category_id: string) => () =>
    onChange(toggleCategoryInGoal({ goal, category_id }));

  const onChangeColor = (color: string) => {
    onChange({ ...goal, color });
    updateReminders({ color });
  };

  return (
    <Screen>
      <Header>
        <ScreenBackButton onPress={onCancel} />
        <ScreenTitle>{headerText}</ScreenTitle>
      </Header>

      <Body onScroll={scrollHandler} scrollEventThrottle={16}>
        <BodyContent>
          {show_suggestions ? (
            !isSmall ? (
              <SuggestionsPanel>
                <SuggestionsTextContainer>
                  <Text weight="semibold">Suggestions</Text>
                  <FontAwesomeIcon icon="stars" />
                </SuggestionsTextContainer>

                {loadingSuggestions ? (
                  <FullScreenLoader />
                ) : (
                  goal_suggestions?.map((suggestion) => (
                    <SuggestionCard
                      key={suggestion.id}
                      goal={suggestion}
                      onPress={onPressSuggestion}
                    />
                  ))
                )}

                <View>
                  <Button
                    variant="secondary"
                    size="xlarge"
                    onPress={onPressViewSuggestions}
                  >
                    See more
                  </Button>
                </View>
              </SuggestionsPanel>
            ) : (
              <SuggestionsPanel>
                <Button
                  variant="secondary"
                  size="xlarge"
                  onPress={onPressViewSuggestions}
                  style={{ flex: 1 }}
                >
                  <Text weight="bold">See Suggestions</Text>
                  <FontAwesomeIcon icon="stars" />
                </Button>
              </SuggestionsPanel>
            )
          ) : null}

          <TitleCard isSmall={isSmall}>
            <EmojiInputField
              emoji={goal.emoji || '🎯'}
              onChange={onChangeEmoji}
              backgroundColor="#EEEEFE"
            />

            <LargeInput
              placeholder="Goal name"
              value={goal.title}
              onChangeText={onChangeTitle}
              style={{ width: '100%' }}
            />
          </TitleCard>

          {categories ? (
            <Row>
              {categories.map((option) => (
                <React.Fragment key={option.id}>
                  <TouchableOpacity onPress={onPressCategory(option.id)}>
                    <Chip
                      iconRight={option.icon}
                      chipVariant={
                        goal.category_ids.includes(option.id)
                          ? 'active'
                          : 'grey'
                      }
                    >
                      {option.label}
                    </Chip>
                  </TouchableOpacity>
                </React.Fragment>
              ))}
            </Row>
          ) : null}

          <Card>
            <TouchableOpacity onPress={toggleShowPeriods}>
              <CardRow>
                <CardRowLeft icon="calendar-day">
                  <Text>Repeat</Text>
                </CardRowLeft>

                <Row alignItems="center">
                  <Text>{getGoalPeriodText(goal.period)}</Text>
                  <Spacer width="16px" />
                  <FontAwesomeIcon
                    icon={period_visible ? 'chevron-up' : 'chevron-down'}
                    size={22}
                  />
                </Row>
              </CardRow>
            </TouchableOpacity>

            {period_visible ? (
              <Row padding={16}>
                <PeriodChip
                  chipVariant={
                    goal.period === GoalPeriod.day ? 'active' : 'grey'
                  }
                  onPress={onPressPeriod(GoalPeriod.day)}
                >
                  Daily
                </PeriodChip>
                <Spacer width="16px" />
                <PeriodChip
                  chipVariant={
                    goal.period === GoalPeriod.week ? 'active' : 'grey'
                  }
                  onPress={onPressPeriod(GoalPeriod.week)}
                >
                  Weekly
                </PeriodChip>
                <Spacer width="16px" />
                <PeriodChip
                  chipVariant={
                    goal.period === GoalPeriod.month ? 'active' : 'grey'
                  }
                  onPress={onPressPeriod(GoalPeriod.month)}
                >
                  Monthly
                </PeriodChip>
              </Row>
            ) : null}

            <Spacer height="1px" color="lightgrey" />

            <CardRow>
              <CardRowLeft icon="trophy">
                <Text>{goal.getFrequencyDisplay()}</Text>
              </CardRowLeft>

              <Row>
                <NewButton
                  icon={['far', 'minus']}
                  variant="secondary"
                  onPress={onUpdateCount(-1)}
                />
                <Spacer width="16px" />
                <NewButton
                  icon="plus"
                  variant="secondary"
                  onPress={onUpdateCount(1)}
                />
              </Row>
            </CardRow>
          </Card>

          {goal.isTemplate ? (
            <Card>
              <CardRow>
                <TitleInput
                  placeholder="Reference Code"
                  value={goal.reference_code || ''}
                  onChangeText={onChangeReferenceCode}
                />
              </CardRow>
            </Card>
          ) : null}

          <PaddedCard>
            <FormColorSection
              expanded={colors_expanded}
              section={0}
              value={goal.color}
              onPress={colors_expanded ? collapseColors : expandColors}
              onChange={onChangeColor}
            />
          </PaddedCard>

          {!goal.isTemplate && onAddReminder ? (
            <Card>
              <TouchableOpacity onPress={toggleShowReminders}>
                <CardRow>
                  <CardRowLeft icon="calendar-check">
                    <Text>Remind me at time</Text>
                  </CardRowLeft>

                  <View>
                    <FontAwesomeIcon
                      icon={reminders_visible ? 'chevron-up' : 'chevron-down'}
                      size={22}
                    />
                  </View>
                </CardRow>
              </TouchableOpacity>

              {reminders_visible ? (
                <>
                  {/* Figure out where these reminders come from */}
                  <RemindersContainer>
                    {reminders?.map((reminder, index) => (
                      <>
                        <Spacer
                          key={`spacer_${reminder.id}_${index}`}
                          height="1px"
                          color="lightgrey"
                        />
                        <ReminderFormRow
                          key={`row_${reminder.id}_${index}`}
                          reminder={reminder}
                          onChange={onUpdateReminder}
                          onPressDelete={onDeleteReminder}
                        />
                      </>
                    ))}
                  </RemindersContainer>

                  <Spacer height="1px" color="lightgrey" />

                  <TouchableOpacity onPress={onAddReminder}>
                    <CardRow>
                      <CardRowLeft icon="plus">
                        <Text>Add reminder</Text>
                      </CardRowLeft>
                    </CardRow>
                  </TouchableOpacity>
                </>
              ) : null}
            </Card>
          ) : null}
        </BodyContent>
      </Body>

      <FloatingActions style={floating_action_styles}>
        {onDelete ? <ScreenDeleteButton onPress={onDelete} /> : null}
        <ScreenSaveButton onPress={onConfirm} />
      </FloatingActions>
    </Screen>
  );
};

const Screen = styled.View`
  flex: 1;
`;

const Body = styled(Animated.ScrollView)`
  flex: 1;
`;

const BodyContent = styled.View`
  padding: 0 22px 150px;
  gap: 16px;
  overflow: visible;
`;

const Header = styled(ScreenHeader)`
  gap: 20px;
  justify-content: flex-start;
  align-items: center;
`;

const TitleCard = styled(Card)<{ isSmall: boolean }>`
  ${({ isSmall = false }) =>
    isSmall
      ? `flex-direction: column; justify-content: center;`
      : `flex-direction: row;`}
  align-items: center;
  padding: 25px 18px;
  gap: 25px;
`;

const PaddedCard = styled(Card)`
  padding: 16px;
`;

const TitleInput = styled(TextInput)`
  flex: 1;
`;

const CardRow = styled(Row)`
  justify-content: space-between;
  align-items: center;
  padding: 16px;
`;

const CardRowIcon = styled(FontAwesomeIcon)`
  margin-right: 16px;
`;

const RemindersContainer = styled.View`
  padding: 0 16px;

  gap: 8px;
`;

const SuggestionsPanel = styled(Panel)`
  flex-direction: row;
  align-items: center;
  gap: 15px;
  padding: 15px;
`;

const SuggestionsTextContainer = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 5px;

  opacity: 0.7;
`;

const CardRowLeft = (props: { icon?: IconProp; children: React.ReactNode }) => (
  <Row alignItems="center">
    {props.icon && (
      <CardRowIcon icon={props.icon} color="rgba(5, 22, 51, 0.5)" />
    )}
    {props.children}
  </Row>
);

const PeriodChip = styled(Chip)`
  flex: 1;
  text-align: center;
  justify-content: center;
`;

const SuggestionCard = styled(GoalLibraryCard)`
  flex: 1;
`;

export default GoalFormScreenView;
